import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { motion } from 'framer-motion';
import { X, Heart, Send, Pause, Play, RotateCcw, ChevronRight, ImageOff, Eye } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Avatar, AvatarFallback, AvatarImage } from '../ui/avatar';
import { toast } from 'sonner';

const IMAGE_DURATION = 7000;
const TEXT_DURATION = 6000;
const TICK = 50;

export const mediaSrc = (url) => {
  if (!url) return null;
  if (url.startsWith('http') || url.startsWith('blob:') || url.startsWith('data:')) return url;
  return `${process.env.REACT_APP_BACKEND_URL}${url}`;
};

const StoryViewer = ({ stories, startIndex = 0, onClose, api, currentUserId, onStoryUpdate }) => {
  const [storyIndex, setStoryIndex] = useState(startIndex);
  const [slideIndex, setSlideIndex] = useState(0);
  const [progress, setProgress] = useState(0);
  const [manualPause, setManualPause] = useState(false);
  const [holdPause, setHoldPause] = useState(false);
  const [mediaState, setMediaState] = useState('loading'); // loading | ready | error
  const [ended, setEnded] = useState(false);
  const [replyText, setReplyText] = useState('');
  const [replyFocused, setReplyFocused] = useState(false);
  const [sendingReply, setSendingReply] = useState(false);
  const [showViewers, setShowViewers] = useState(false);
  const [viewersData, setViewersData] = useState(null);
  const videoRef = useRef(null);
  const viewedRef = useRef(new Set());

  const story = stories[storyIndex];
  const slide = story?.slides?.[slideIndex];
  const isVideo = slide?.type === 'video' && !!slide?.video_url;
  const isImage = slide?.type === 'image' && !!slide?.image_url;
  const isMedia = isVideo || isImage;

  const duration = useMemo(() => {
    if (isVideo) return null; // driven by the video element itself
    return isImage ? IMAGE_DURATION : TEXT_DURATION;
  }, [isVideo, isImage]);

  const paused = manualPause || holdPause || replyFocused || !!replyText || ended || showViewers || (isMedia && mediaState !== 'ready');
  const isOwnStory = story?.user_id === currentUserId;

  const openViewers = async () => {
    setShowViewers(true);
    try {
      const res = await api.get(`/stories/${story.id}/viewers`);
      setViewersData(res.data);
    } catch {
      setViewersData({ viewers: [], views_count: 0, likes_count: 0, replies_count: 0 });
      toast.error("Impossible de charger les vues");
    }
  };

  // Mark viewed (once per story)
  useEffect(() => {
    if (!story || viewedRef.current.has(story.id)) return;
    viewedRef.current.add(story.id);
    api.post(`/stories/${story.id}/view`).catch(() => {});
  }, [story, api]);

  // Reset per-slide state
  useEffect(() => {
    setProgress(0);
    setEnded(false);
    setShowViewers(false);
    setViewersData(null);
    setMediaState(isMedia ? 'loading' : 'ready');
  }, [storyIndex, slideIndex, isMedia]);

  const goNext = useCallback(() => {
    const slidesCount = story?.slides?.length || 1;
    if (slideIndex < slidesCount - 1) {
      setSlideIndex((i) => i + 1);
      return;
    }
    // End of this member's story: stop here, never silently swap to someone else
    setProgress(100);
    setEnded(true);
  }, [story, slideIndex]);

  const goPrev = useCallback(() => {
    if (slideIndex > 0) setSlideIndex((i) => i - 1);
    else if (storyIndex > 0) {
      setStoryIndex((i) => i - 1);
      setSlideIndex(0);
    } else setProgress(0);
  }, [slideIndex, storyIndex]);

  const goNextStory = useCallback(() => {
    if (storyIndex < stories.length - 1) {
      setStoryIndex((i) => i + 1);
      setSlideIndex(0);
    } else onClose();
  }, [storyIndex, stories.length, onClose]);

  const replay = () => {
    setEnded(false);
    setProgress(0);
    setSlideIndex(0);
    if (videoRef.current) {
      videoRef.current.currentTime = 0;
      videoRef.current.play().catch(() => {});
    }
  };

  // Timer for images / text slides
  useEffect(() => {
    if (isVideo || paused) return;
    const step = (100 * TICK) / duration;
    const id = setInterval(() => {
      setProgress((p) => {
        if (p + step >= 100) {
          clearInterval(id);
          goNext();
          return 100;
        }
        return p + step;
      });
    }, TICK);
    return () => clearInterval(id);
  }, [isVideo, paused, duration, goNext]);

  // Keep the video element in sync with pause state
  useEffect(() => {
    if (!isVideo || !videoRef.current) return;
    if (paused) videoRef.current.pause();
    else videoRef.current.play().catch(() => {});
  }, [paused, isVideo, mediaState]);

  // Keyboard
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowRight' && !replyFocused) goNext();
      if (e.key === 'ArrowLeft' && !replyFocused) goPrev();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose, goNext, goPrev, replyFocused]);

  const toggleLike = async () => {
    try {
      const res = await api.post(`/stories/${story.id}/like`);
      onStoryUpdate?.(story.id, { liked: res.data.liked, likes_count: res.data.likes_count });
    } catch {
      toast.error("Impossible d'aimer ce moment");
    }
  };

  const sendReply = async () => {
    if (!replyText.trim()) return;
    try {
      setSendingReply(true);
      await api.post(`/stories/${story.id}/reply`, { content: replyText.trim() });
      toast.success('Réponse envoyée !');
      setReplyText('');
    } catch {
      toast.error("Impossible d'envoyer la réponse");
    } finally {
      setSendingReply(false);
    }
  };

  const timeAgo = (iso) => {
    if (!iso) return '';
    const mins = Math.floor(Math.max(0, Date.now() - new Date(iso).getTime()) / 60000);
    if (mins < 1) return "À l'instant";
    if (mins < 60) return `Il y a ${mins} min`;
    return `Il y a ${Math.floor(mins / 60)}h`;
  };

  if (!story) return null;

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="fixed inset-0 z-[200] bg-black flex items-center justify-center" onClick={onClose} data-testid="story-viewer">
      <motion.div initial={{ scale: 0.95, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.95, opacity: 0 }}
        className="relative w-full max-w-lg h-[85vh] max-h-[760px] rounded-2xl overflow-hidden bg-black"
        onClick={(e) => e.stopPropagation()}>

        {/* Progress bars */}
        <div className="absolute top-2 left-2 right-2 flex gap-1 z-30">
          {story.slides.map((_, idx) => (
            <div key={idx} className="flex-1 h-1 bg-white/30 rounded-full overflow-hidden">
              <div className="h-full bg-white transition-[width] duration-75 ease-linear"
                style={{ width: idx < slideIndex ? '100%' : idx === slideIndex ? `${progress}%` : '0%' }} />
            </div>
          ))}
        </div>

        {/* Header */}
        <div className="absolute top-6 left-4 right-4 flex items-center justify-between z-30">
          <div className="flex items-center gap-2">
            <Avatar className="w-8 h-8 border border-white/50">
              <AvatarImage src={mediaSrc(story.user_avatar)} />
              <AvatarFallback className="text-xs">{story.user_name?.split(' ').map((n) => n[0]).join('')}</AvatarFallback>
            </Avatar>
            <div>
              <p className="text-white text-sm font-medium">{story.user_id === currentUserId ? 'Toi' : story.user_name}</p>
              <p className="text-white/60 text-xs">{timeAgo(story.created_at)}</p>
            </div>
          </div>
          <div className="flex items-center gap-1">
            <Button variant="ghost" size="icon" className="text-white hover:bg-white/20"
              onClick={() => setManualPause((p) => !p)} data-testid="story-pause-btn">
              {manualPause || ended ? <Play className="w-5 h-5" /> : <Pause className="w-5 h-5" />}
            </Button>
            <Button variant="ghost" size="icon" className="text-white hover:bg-white/20" onClick={onClose} data-testid="story-close-btn">
              <X className="w-5 h-5" />
            </Button>
          </div>
        </div>

        {/* Slide content */}
        <div className={`w-full h-full flex items-center justify-center ${isMedia ? 'bg-black' : `bg-gradient-to-br ${slide?.background || 'from-slate-800 to-slate-900'} p-8`}`}>
          {isImage ? (
            <>
              <img
                key={slide.image_url}
                src={mediaSrc(slide.image_url)}
                alt=""
                className={`max-w-full max-h-full object-contain ${mediaState === 'ready' ? 'opacity-100' : 'opacity-0'}`}
                onLoad={() => setMediaState('ready')}
                onError={() => setMediaState('error')}
                data-testid="story-image"
              />
              {mediaState === 'loading' && <div className="absolute w-8 h-8 border-2 border-white/30 border-t-white rounded-full animate-spin" />}
            </>
          ) : isVideo ? (
            <video
              key={slide.video_url}
              ref={videoRef}
              src={mediaSrc(slide.video_url)}
              autoPlay
              playsInline
              className="max-w-full max-h-full object-contain"
              onLoadedData={() => setMediaState('ready')}
              onError={() => setMediaState('error')}
              onTimeUpdate={(e) => {
                const el = e.currentTarget;
                if (el.duration) setProgress(Math.min(100, (el.currentTime / el.duration) * 100));
              }}
              onEnded={goNext}
              data-testid="story-video"
            />
          ) : (
            <p className="text-white text-2xl font-bold text-center leading-relaxed drop-shadow-lg" data-testid="story-text">{slide?.content}</p>
          )}

          {isMedia && mediaState === 'error' && (
            <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 px-8 text-center bg-slate-900" data-testid="story-media-error">
              <ImageOff className="w-10 h-10 text-white/50" />
              <p className="text-white/80 text-sm">Ce média n'est plus disponible</p>
              {slide?.content && <p className="text-white text-lg font-semibold">{slide.content}</p>}
            </div>
          )}
        </div>

        {/* Caption over media */}
        {isMedia && slide?.content && mediaState !== 'error' && (
          <div className="absolute bottom-20 left-4 right-4 z-20 pointer-events-none">
            <p className="text-white text-base font-medium drop-shadow-lg bg-black/40 rounded-xl px-3 py-2 inline-block">{slide.content}</p>
          </div>
        )}

        {/* Tap zones (hold to pause) */}
        <div className="absolute inset-x-0 top-16 bottom-24 flex z-10">
          <div className="w-1/3 h-full cursor-pointer" onClick={goPrev}
            onPointerDown={() => setHoldPause(true)} onPointerUp={() => setHoldPause(false)} onPointerLeave={() => setHoldPause(false)}
            data-testid="story-prev-zone" />
          <div className="w-1/3 h-full" onPointerDown={() => setHoldPause(true)} onPointerUp={() => setHoldPause(false)} onPointerLeave={() => setHoldPause(false)} />
          <div className="w-1/3 h-full cursor-pointer" onClick={goNext}
            onPointerDown={() => setHoldPause(true)} onPointerUp={() => setHoldPause(false)} onPointerLeave={() => setHoldPause(false)}
            data-testid="story-next-zone" />
        </div>

        {/* End of story overlay: nothing auto-swaps anymore */}
        {ended && (
          <div className="absolute inset-0 z-30 bg-black/70 backdrop-blur-sm flex flex-col items-center justify-center gap-3" data-testid="story-ended-overlay">
            <p className="text-white/80 text-sm mb-1">Moment terminé</p>
            <Button variant="outline" className="bg-white/10 border-white/30 text-white hover:bg-white/20 w-44" onClick={replay} data-testid="story-replay-btn">
              <RotateCcw className="w-4 h-4 mr-2" /> Revoir
            </Button>
            {storyIndex < stories.length - 1 && (
              <Button variant="outline" className="bg-white/10 border-white/30 text-white hover:bg-white/20 w-44" onClick={goNextStory} data-testid="story-next-story-btn">
                <ChevronRight className="w-4 h-4 mr-2" /> Moment suivant
              </Button>
            )}
            <Button variant="ghost" className="text-white/70 hover:bg-white/10 w-44" onClick={onClose} data-testid="story-end-close-btn">Fermer</Button>
          </div>
        )}

        {/* Viewers panel (author only) */}
        {showViewers && (
          <div className="absolute inset-0 z-50 bg-black/85 backdrop-blur-sm flex flex-col" onClick={(e) => e.stopPropagation()} data-testid="story-viewers-panel">
            <div className="flex items-center justify-between px-4 py-3 border-b border-white/10">
              <div className="flex items-center gap-2 text-white">
                <Eye className="w-4 h-4" />
                <span className="text-sm font-medium">
                  {viewersData ? `${viewersData.views_count} vue${viewersData.views_count > 1 ? 's' : ''}` : 'Chargement...'}
                </span>
                {viewersData?.likes_count > 0 && (
                  <span className="flex items-center gap-1 text-sm text-white/70 ml-2">
                    <Heart className="w-3.5 h-3.5 fill-red-500 text-red-500" /> {viewersData.likes_count}
                  </span>
                )}
              </div>
              <Button variant="ghost" size="icon" className="text-white hover:bg-white/20" onClick={() => setShowViewers(false)} data-testid="story-viewers-close-btn">
                <X className="w-5 h-5" />
              </Button>
            </div>
            <div className="flex-1 overflow-y-auto px-4 py-2">
              {viewersData && viewersData.viewers.length === 0 && (
                <p className="text-white/60 text-sm text-center py-8">Personne n'a encore vu ce moment</p>
              )}
              {viewersData?.viewers.map((v) => (
                <div key={v.id} className="flex items-center gap-3 py-2.5 border-b border-white/5" data-testid={`story-viewer-${v.id}`}>
                  <Avatar className="w-9 h-9 border border-white/20">
                    <AvatarImage src={mediaSrc(v.avatar_url)} />
                    <AvatarFallback className="text-xs">{`${v.first_name?.[0] || ''}${v.last_name?.[0] || ''}`}</AvatarFallback>
                  </Avatar>
                  <span className="text-white text-sm flex-1">{v.first_name} {v.last_name}</span>
                  {v.liked && <Heart className="w-4 h-4 fill-red-500 text-red-500" />}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Actions */}
        <div className="absolute bottom-4 left-4 right-4 flex items-center gap-2 z-40" onClick={(e) => e.stopPropagation()}>
          {isOwnStory ? (
            <Button
              variant="outline"
              className="bg-white/10 border-white/30 text-white hover:bg-white/20"
              onClick={openViewers}
              data-testid="story-viewers-btn"
            >
              <Eye className="w-4 h-4 mr-2" />
              {story.views_count || 0} vue{(story.views_count || 0) > 1 ? 's' : ''}
              {story.likes_count > 0 && (
                <span className="flex items-center gap-1 ml-3 text-white/80">
                  <Heart className="w-3.5 h-3.5 fill-red-500 text-red-500" /> {story.likes_count}
                </span>
              )}
            </Button>
          ) : (
            <>
              <Input
                placeholder="Répondre..."
                value={replyText}
                onChange={(e) => setReplyText(e.target.value)}
                onFocus={() => setReplyFocused(true)}
                onBlur={() => setReplyFocused(false)}
                onKeyDown={(e) => { if (e.key === 'Enter') sendReply(); }}
                className="story-reply-field backdrop-blur-sm"
                data-testid="story-reply-input"
              />
              <div className="flex flex-col items-center">
                <Button size="icon" variant="ghost" className="text-white hover:bg-white/20" onClick={toggleLike} data-testid="story-like-btn">
                  <Heart className={`w-5 h-5 ${story.liked ? 'fill-red-500 text-red-500' : ''}`} />
                </Button>
                {story.likes_count > 0 && <span className="text-white/80 text-[10px] -mt-1" data-testid="story-likes-count">{story.likes_count}</span>}
              </div>
              <Button size="icon" variant="ghost" className="text-white hover:bg-white/20" onClick={sendReply} disabled={sendingReply || !replyText.trim()} data-testid="story-reply-send-btn">
                <Send className="w-5 h-5" />
              </Button>
            </>
          )}
        </div>
      </motion.div>
    </motion.div>
  );
};

export default StoryViewer;
